import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { Flex } from 'rebass/styled-components';
import _ from 'lodash';

import { useObserver } from 'mobx-react';
import ReactSelect from 'shared/components/react-select';

import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import {
  RadioGroup,
  CheckoutAccordion,
  CheckoutAccordionActions,
  CheckoutAccordionSummary,
  Image,
} from 'shared/components';

import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import { useGetAvailableReservations } from 'src/state/actions/cart';
import {
  getAdvancedDayOrderingDays,
  getAdvancedDayOrderingTimes,
  sameDayDeliveryAdjustedOrderingDays,
} from 'shared/helpers/order-reservations';
import { getServiceEstimateForCart } from 'shared/helpers/order-estimates';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import { shouldHideOrderEstimates } from 'shared/helpers/dispensaries';

function ReactSelectOptionLabel({ label, isDisabled, labelPrefix }) {
  return (
    <span>
      <>
        {labelPrefix}: <Bold>{label}</Bold>
        {isDisabled ? ' (Sold out)' : ''}
      </>
    </span>
  );
}

function ReactSelectOptionLabelAdvancedDay({ label, isDisabled }) {
  return (
    <span>
      <>
        {`${label} `}
        {isDisabled ? (
          <>
            <br />
            <span className='sold-out'>(Sold out)</span>
          </>
        ) : (
          ''
        )}
      </>
    </span>
  );
}

const NoOptionsAvailable = (message) => <NoOptionsMessage>{message}</NoOptionsMessage>;

const getPrefix = (advancedDayOrderingEnabled, formattedOption) => {
  if (
    advancedDayOrderingEnabled &&
    formattedOption.labelPrefix !== 'Today' &&
    formattedOption.labelPrefix !== 'Tomorrow'
  ) {
    return formattedOption.dayOfWeek;
  }
  return formattedOption.labelPrefix;
};

const SELECT_CONTAINER_STYLES = {
  display: 'flex',
  width: '100%',
};

const SELECT_OPTION_STYLES = {
  height: '40px',
  padding: 'unset',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '20px',
};

function ScheduledOrders(_props, ref) {
  const { customized } = useTheme();
  const Checkout = useCheckout();
  const { Cart } = useStores();
  const amplitude = useAmplitude();
  const dispensary = useObserver(() => Cart?.dispensary || Cart?.order?.dispensary);
  const getAvailableReservations = useGetAvailableReservations();
  const checkoutReservationOptions = useObserver(() => Cart.checkoutReservationOptions);
  const reservation = useObserver(() => Cart.order.reservation);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState();
  const [tempIndex, setTempIndex] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const orderType = useObserver(() => (Cart.orderType === 'pickup' ? 'inStorePickup' : Cart.orderType));
  const cartAddress = useObserver(() => Cart.address);
  const selectedOrderTypeConfigs = dispensary.orderTypesConfigV2[orderType];

  const sameDayDeliveryEnabled = selectedOrderTypeConfigs?.scheduledOrderingConfigV2?.sameDayDeliveryEnabled;
  const asapAndScheduledAvailable =
    selectedOrderTypeConfigs?.enableASAPOrdering && selectedOrderTypeConfigs?.enableScheduledOrdering;
  const showAsapOption = asapAndScheduledAvailable && !Cart.isAfterHoursOrder;
  const disableScheduledOrdering = !cartAddress.ln1 && orderType === 'delivery';
  const scheduledLabel = disableScheduledOrdering ? 'Scheduled For Later (Address required)' : 'Scheduled For Later';
  const defaultSchedulingOption = showAsapOption ? 'asap' : 'scheduled';
  const [selectedSchedulingOption, setSelectedSchedulingOption] = useState(defaultSchedulingOption);

  const advancedDayOrderingEnabled = selectedOrderTypeConfigs?.scheduledOrderingConfigV2.advancedDayOrderingEnabled;
  const advancedDayOrderingLimit = selectedOrderTypeConfigs?.scheduledOrderingConfigV2.advancedDayOrderingLimit;

  const timezone = dispensary?.timezone;
  const orderTypeDisplayName = orderType === 'delivery' ? orderType : 'pickup';
  const serviceEstimate = getServiceEstimateForCart(Cart);

  const { updateCartSchedulingInfo } = _props;
  const hideOrderEstimates = shouldHideOrderEstimates(dispensary, Cart, true);
  const hideDeliveryEstimates = hideOrderEstimates && orderType === 'delivery';

  const schedulingOptions = [
    {
      label: 'ASAP',
      sublabel: hideDeliveryEstimates ? '' : `(${serviceEstimate}s)`,
      value: 'asap',
      disabled: Cart.isAfterHoursOrder,
      dataCyName: 'asap-option',
    },
    {
      label: scheduledLabel,
      value: 'scheduled',
      disabled: disableScheduledOrdering,
      dataCyName: 'scheduled-option',
    },
  ];

  const advancedDayOrderingDays = useMemo(() => {
    if (advancedDayOrderingEnabled) {
      if (!sameDayDeliveryEnabled && orderType === 'delivery') {
        return sameDayDeliveryAdjustedOrderingDays(advancedDayOrderingLimit, timezone);
      }
      return getAdvancedDayOrderingDays({
        advancedDayOrderingLimit,
        timezone,
      });
    }
    return [];
  }, [advancedDayOrderingEnabled, advancedDayOrderingLimit, orderType, sameDayDeliveryEnabled, timezone]);

  const reservationDropdownOptions = useMemo(() => {
    if (advancedDayOrderingEnabled) {
      return getAdvancedDayOrderingTimes({
        advancedDayOrderingDays,
        reservationOptions: checkoutReservationOptions,
        selectedDayIndex,
      });
    }
    return checkoutReservationOptions;
  }, [advancedDayOrderingDays, checkoutReservationOptions, selectedDayIndex, advancedDayOrderingEnabled]);

  const existingReservationIndex = _.findIndex(checkoutReservationOptions, {
    startTimeISO: reservation?.startTimeISO,
  });

  const [open, setOpen] = useState(!existingReservationIndex);

  getAvailableReservations();

  useEffect(() => {
    if (!Cart.freezeDeliveryInfo) {
      resetReservation();
      setSelectedSchedulingOption(defaultSchedulingOption);
      updateCartSchedulingInfo(defaultSchedulingOption, checkoutReservationOptions);
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, cartAddress]);

  useEffect(() => {
    if (existingReservationIndex >= 0) {
      const checkoutReservationObject = checkoutReservationOptions?.[existingReservationIndex];
      setSelectedIndex(existingReservationIndex);
      setSelectedReservation(checkoutReservationObject?.reservation);
      const newSelectedDayIndex = _.findIndex(advancedDayOrderingDays, [
        'dayOfWeek',
        checkoutReservationObject?.dayOfWeek,
      ]);
      if (newSelectedDayIndex >= 0) {
        setSelectedDayIndex(newSelectedDayIndex);
      }
      if (_.isNil(tempIndex)) {
        setOpen(false);
      }
    }
  }, [existingReservationIndex, checkoutReservationOptions, advancedDayOrderingDays, tempIndex]);

  function resetReservation() {
    setSelectedIndex(null);
    setSelectedDayIndex(0);
    setSelectedReservation(null);
    setTempIndex(null);
  }

  const handleError = () => {
    Checkout.addErrors([{ path: 'reservation', message: 'Please select a time for this order.' }]);
    setOpen(true);
    return false;
  };

  const handleDayChange = (option) => {
    const { value } = option;
    setSelectedIndex(null);
    setSelectedDayIndex(value);
  };

  const handleTimeChange = (option) => {
    const { value } = option;
    const selectedOption = checkoutReservationOptions[value];

    setSelectedReservation(selectedOption?.reservation);
    setSelectedIndex(selectedOption.value);
    Checkout.removeError({ match: 'reservation' });
  };

  const handleClose = () => {
    Checkout.removeError({ match: 'reservation' });
    setOpen(false);
  };

  const handleSave = async () => {
    if (selectedSchedulingOption === 'scheduled') {
      amplitude.log(AmplitudeEvents.checkout.clickSave, {
        componentName: `Time Slot`,
        dispensaryId: dispensary?.id,
        dispensaryName: dispensary?.name,
      });
      if (_.isNil(selectedIndex) || (!selectedReservation && _.isEmpty(Cart.order.reservation))) {
        handleError();
        throw new Error(`onSave error: 'Please select a time for this order.'`);
      }
      Cart.setReservation(selectedReservation);
    } else {
      amplitude.log(AmplitudeEvents.checkout.clickSave, {
        componentName: `Asap Order`,
        dispensaryId: dispensary?.id,
        dispensaryName: dispensary?.name,
      });
      Cart.setReservation(null);
    }

    Cart.setReservationType(selectedSchedulingOption);
    updateCartSchedulingInfo(selectedSchedulingOption, checkoutReservationOptions);
    setTempIndex(null);
    handleClose();
  };

  // eslint-disable-next-line consistent-return
  useImperativeHandle(ref, () => ({ handleSave }));

  const handleCancel = () => {
    // must have a reservation to cancel changes.
    // otherwise show ernie
    if (selectedSchedulingOption === 'scheduled') {
      if (_.isEmpty(Cart.order.reservation) && _.isNil(tempIndex)) {
        return handleError();
      }
      if (!_.isNil(tempIndex)) {
        setSelectedIndex(tempIndex);
        const newSelectedReservation = checkoutReservationOptions?.[tempIndex]?.reservation;
        setSelectedReservation(newSelectedReservation);
        Cart.setReservation(newSelectedReservation);
        setTempIndex(null);
      } else {
        Cart.setReservation(null);
      }
    }
    return handleClose();
  };

  const handleSchedulingOptionChange = (value) => {
    resetReservation();
    setSelectedSchedulingOption(value);
    updateCartSchedulingInfo(value, checkoutReservationOptions);
  };

  const formattedOption = checkoutReservationOptions[selectedIndex];
  const timeSelectionText = Cart.orderType === 'delivery' ? 'your order delivered' : 'to pickup your order';
  const timeSummaryLabel = _.find(schedulingOptions, { value: selectedSchedulingOption })?.label;
  const timeSummarySubLabel = _.find(schedulingOptions, { value: selectedSchedulingOption })?.sublabel;

  return useObserver(() => (
    <CheckoutLabelGrouping mb='20px' id='scheduled-orders' data-testid='scheduled-orders-section'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Time</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        <CheckoutAccordion
          dataCyName='order-type'
          expanded={open}
          onExpand={() => {
            if (!open && selectedIndex) {
              setTempIndex(selectedIndex);
              Cart.setReservation(null);
            }
            setOpen(!open);
          }}
          onSave={handleSave}
          onCancel={handleCancel}
          error={Checkout.getError({ card: 'scheduled-orders' }).error}
          crossfade
        >
          <CheckoutAccordionSummary>
            <Flex mb={0.5} width='100%'>
              {!open && (
                <>
                  <Image src='/icons/clock.svg' width='20px' mr='11px' />
                  <SummaryText>
                    {checkoutReservationOptions[selectedIndex] && (
                      <>
                        <span>{getPrefix(advancedDayOrderingEnabled, formattedOption)}:</span>
                        <strong>&nbsp;{formattedOption?.label}</strong>
                      </>
                    )}
                    {!checkoutReservationOptions[selectedIndex] && (
                      <>
                        <strong>{timeSummaryLabel}</strong>
                        {timeSummarySubLabel && <span> {timeSummarySubLabel}</span>}
                      </>
                    )}
                  </SummaryText>
                </>
              )}
            </Flex>
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <InnerSummaryText fontSize={13}>
              {showAsapOption ? `When would you like ${timeSelectionText}?` : `Select a ${orderTypeDisplayName} time:`}
            </InnerSummaryText>
            {showAsapOption && (
              <RadioGroup
                options={schedulingOptions}
                hasSublabels
                display='block'
                defaultValue='asap'
                checked={selectedSchedulingOption}
                onChange={handleSchedulingOptionChange}
                mb='12px'
                name='orderTime'
                showChecksWhenDisabled
                required
              />
            )}
            {!(showAsapOption && selectedSchedulingOption === 'asap') && (
              <SelectionContainerV2
                advancedDayOrderingEnabled={advancedDayOrderingEnabled}
                selectedSchedulingOption={selectedSchedulingOption}
              >
                {advancedDayOrderingEnabled && (
                  <SelectContainer advancedDayOrderingEnabled={advancedDayOrderingEnabled}>
                    <ReactSelect
                      onChange={handleDayChange}
                      placeholder='Select a day...'
                      options={advancedDayOrderingDays}
                      noOptionsMessage={() => NoOptionsAvailable(`No days available`)}
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      formatOptionLabel={
                        advancedDayOrderingEnabled ? ReactSelectOptionLabelAdvancedDay : ReactSelectOptionLabel
                      }
                      borderRadius={customized.radius.sm}
                      containerStyles={SELECT_CONTAINER_STYLES}
                      optionStyles={SELECT_OPTION_STYLES}
                      customSelectedBackgroundColor='var(--dcm-color-surface-hover, #F6F6F6)'
                      customSelectedColor='var(--dcm-checkout-text-color, #5D666D)'
                      width='100%'
                      tagPrefix='day-select'
                    />
                  </SelectContainer>
                )}
                <SelectContainer
                  advancedDayOrderingEnabled={advancedDayOrderingEnabled}
                  hasError={!!Checkout.getError({ match: 'reservation' }).error}
                >
                  <ReactSelect
                    placeholder={advancedDayOrderingEnabled ? 'Select a time...' : 'Please select a time...'}
                    onChange={handleTimeChange}
                    options={reservationDropdownOptions}
                    noOptionsMessage={() => NoOptionsAvailable(`No times available`)}
                    isMulti={false}
                    isSearchable={false}
                    isClearable={false}
                    formatOptionLabel={
                      advancedDayOrderingEnabled ? ReactSelectOptionLabelAdvancedDay : ReactSelectOptionLabel
                    }
                    borderRadius={customized.radius.sm}
                    containerStyles={SELECT_CONTAINER_STYLES}
                    optionStyles={SELECT_OPTION_STYLES}
                    customSelectedBackgroundColor='var(--dcm-color-surface-hover, #F6F6F6)'
                    customSelectedColor='var(--dcm-checkout-text-color, #5D666D)'
                    width='100%'
                    hasError={!!Checkout.getError({ match: 'reservation' }).error}
                    tagPrefix='time-select'
                  />
                </SelectContainer>
              </SelectionContainerV2>
            )}
          </CheckoutAccordionActions>
        </CheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  ));
}

const SelectContainer = styled.div`
  display: flex;
  width: ${({ advancedDayOrderingEnabled }) => (advancedDayOrderingEnabled ? '168px' : '100%')};
  @media (max-width: 450px) {
    display: flex;
    width: 100%;
  }
`;

const SummaryText = styled.span`
  color: var(--dcm-checkout-text-color, #5d666d);
  font-size: 14px;
  padding-top: 2px;

  strong {
    font-weight: bold;
  }
`;

const InnerSummaryText = styled(SummaryText)`
  display: inline-block;
  margin-bottom: 20px;

  @media (max-width: 450px) {
    max-width: 80%;
  }
`;

const Bold = styled.strong`
  font-weight: bold;
`;

const SelectionContainerV2 = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-direction: row;
  margin-bottom: 20px;
  @media (max-width: 450px) {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    height: 100px;
  }
`;

const NoOptionsMessage = styled.div`
  color: #98a2a9;
  font-size: 13px;
  line-height: 20px;
  text-align: center;
`;

export default forwardRef(ScheduledOrders);
